import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

export interface SessionData {
  sessionId: string;
  username: string;
  source: string;
  userType: string;
  loginTimeInIST: string;
  loginTimeInCT: string;
  loginTimeInUTC: string;
  logoutTimeInUTC: string | null;
  logoutTimeInCT: string;
  logoutTimeInIST: string;
}

@Injectable({
  providedIn: 'root',
})
export class SessionDataService {
  userManagementDomain: string;
  private domain = environment.userActivityAPI;

  constructor(private http: HttpClient) {
    this.domain = SessionDataService.normalizeUserActivityApi(
      environment.userActivityAPI
    );
    this.userManagementDomain = SessionDataService.derivePortalBaseUrl(
      environment.workSpaceAPI
    );
  }

  /** Ensures user-activity URLs always use the value from environment (prod/uat/dev). */
  private static normalizeUserActivityApi(url: string): string {
    const trimmed = (url || '').trim();
    return trimmed.endsWith('/') ? trimmed : `${trimmed}/`;
  }

  private static derivePortalBaseUrl(workSpaceApi: string): string {
    return (workSpaceApi || '').replace(/\/api\/?$/, '/');
  }

  // getSessions(): Observable<SessionData[]> {
  //   return this.http.get<{ status: number, message: string, data: SessionData[] }>(this.apiUrl)
  //     .pipe(
  //       map(response => response.data)
  //     );
  // }

  getHistorySummaryMetrics(payload: any) {
    const timeZone = this.getTimezoneAbbreviations();
    const url = `${this.domain}summary?fromDate=${payload.fromDate}&toDate=${payload.toDate}&timezone=${timeZone}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getInstantMetrics(payload: any) {
    const timeZone = this.getTimezoneAbbreviations();
    const url = `${this.domain}summary?timezone=${timeZone}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getHistoryGraphMetrics(payload: any) {
    const timeZone = this.getTimezoneAbbreviations();
    const url = `${this.domain}graph?fromDate=${payload.fromDate}&toDate=${payload.toDate}&timezone=${timeZone}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getAllActivityDetails(payload: any) {
    const timeZone = this.getTimezoneAbbreviations();
    let url = `${this.domain}activity/get?page=${payload.pageIndex}&pv=${payload.pv}&source=${payload.source}&pageType=${payload.pageType}&subType=${payload.subType}&userType=${payload.userType}&size=${payload.pageSize}&query=${payload.query}&fromDate=${payload.fromDate}&toDate=${payload.toDate}&timezone=${timeZone}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getSessionIdDetails(payload: any) {
    const timeZone = this.getTimezoneAbbreviations();
    let url = `${this.domain}byid?sessionid=${payload.sessionId}&page=${payload.pageIndex}&size=${payload.pageSize}&timezone=${timeZone}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getUserActivityCSV(payload: any) {
    const timeZone = this.getTimezoneAbbreviations();
    const params = new HttpParams()
      .set('page', String(payload.pageIndex ?? 0))
      .set('size', String(payload.pageSize ?? ''))
      .set('pv', payload.pv ?? '')
      .set('source', payload.source ?? '')
      .set('pageType', payload.pageType ?? '')
      .set('subType', payload.subType ?? '')
      .set('userType', payload.userType ?? '')
      .set('query', payload.query ?? '')
      .set('fromDate', payload.fromDate ?? '')
      .set('toDate', payload.toDate ?? '')
      .set('timezone', timeZone)
      .set('userState', payload.userState ?? '')
      .set('kmeloggedin', payload.kmeloggedin ?? '')
      .set('country', payload.country ?? '')
      .set('csv', 'true');
    return this.http.get(`${this.domain}activity/get`, {
      params,
      responseType: 'blob',
    });
  }

  getUserDataCSV(payload: any) {
    const timeZone = this.getTimezoneAbbreviations();
    const params = new HttpParams()
      .set('page', String(payload.pageIndex ?? 0))
      .set('size', String(payload.pageSize ?? ''))
      .set('query', payload.query ?? '')
      .set('fromDate', payload.fromDate ?? '')
      .set('toDate', payload.toDate ?? '')
      .set('source', payload.source ?? '')
      .set('userType', payload.userType ?? '')
      .set('userState', payload.userState ?? '')
      .set('pv', payload.pv ?? '')
      .set('pageType', payload.pageType ?? '')
      .set('subType', payload.subType ?? '')
      .set('authType', payload.authType ?? '')
      .set('kmeloggedin', payload.kmeloggedin ?? '')
      .set('country', payload.country ?? '')
      .set('timezone', timeZone)
      .set('unique', String(!!payload.unique))
      .set('csv', 'true');
    return this.http.get(`${this.domain}log/get`, {
      params,
      responseType: 'blob',
    });
  }

  getUserMetrics(payload: any): Observable<SessionData[]> {
    const timeZone = this.getTimezoneAbbreviations();
    let apiUrl = `${this.domain}log/get?page=${payload.pageIndex}&size=${payload.pageSize}&query=${payload.query}&fromDate=${payload.fromDate}&toDate=${payload.toDate}&source=${payload.source}&userType=${payload.userType}&userState=${payload.userState}&pv=${payload.pv}&pageType=${payload.pageType}&subType=${payload.subType}&authType=${payload.authType}&kmeloggedin=${payload.kmeloggedin}&country=${payload?.country}&timezone=${timeZone}&unique=${payload.unique}`;
    return this.http
      .get<{ status: number; message: string; data: SessionData[] }>(apiUrl)
      .pipe(map((response) => response.data));
  }

  getTimezoneAbbreviations(): string {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const timezoneAbbreviations: { [key: string]: string } = {
      'America/Chicago': 'CT',
      'America/Winnipeg': 'CT',
      'America/Los_Angeles': 'PT',
      'America/Vancouver': 'PT',
      'Asia/Calcutta': 'IST',
      'Asia/Kolkata': 'IST',
    };
    const timezoneAbbreviation = timezoneAbbreviations[userTimezone] || 'IST';
    return timezoneAbbreviation;
  }

  flattenObject(obj, parentKey = '', result = {}) {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        let newKey =
          parentKey && parentKey !== 'additionalInfo'
            ? `${parentKey}_${key}`
            : key;

        if (typeof obj[key] === 'object' && obj[key] !== null) {
          this.flattenObject(obj[key], newKey, result);
        } else {
          result[newKey] = obj[key];
        }
      }
    }
    return result;
  }

  getUserDetails(payload: any): Observable<any> {
    let url = `${environment.workSpaceAPI}/user/fetch?page=${payload.page}&size=${payload.size}&username=${payload.username}&usertype=${payload.usertype}&status=${payload.status}&user=${payload.user}&verified=${payload.verified}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getUserDetailsById(payload: any): Observable<any> {
    let url = `${environment.workSpaceAPI}/user/username?username=${payload.username}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getSiteDetails(payload: any): Observable<any> {
    const url = `${environment.workSpaceAPI}/user/getSites?page=${payload.page}&size=${payload.size}&userType=${payload.userType}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getSiteAddressDetails(payload: any): Observable<any> {
    const url = `${environment.workSpaceAPI}/user/getSites?search=${payload.searchKey}&page=${payload.page}&size=${payload.size}&userType=${payload.userType}`;
    return this.http
      .get<{ status: number; message: string; data: any }>(url)
      .pipe(map((response) => response.data));
  }

  getUserCSV(payload: any): Observable<Blob> {
    let apiUrl = `${environment.workSpaceAPI}/user/fetch?page=${payload.page}&size=${payload.size}&username=${payload.username}&usertype=${payload.usertype}&status=${payload.status}&user=${payload.user}&verified=${payload.verified}&csv=true`;
    return this.http.get(apiUrl, { responseType: 'blob' });
  }

  getUsersByAccount(url: string): Observable<any[]> {
    return this.http.get<any[]>(url);
  }

  getUsersByPotentialId(potentialId: string): Observable<any> {
    const url = `${environment.restrictUserDomain}/usercammapping/getusersbypotentialid?potentialId=${potentialId}`;
    return this.http.get(url);
  }

  getUsersByAll(search: string): Observable<any> {
    const url = `${environment.workSpaceAPI}/user/getSitesByAll?search=${search}`;
    return this.http.get(url);
  }

  getUserFeatures(username: string): Observable<any> {
    const url = `${environment.restrictUserDomain}/userfeaturemap/getuserfeatures?username=${encodeURIComponent(username)}`;
    return this.http.get(url);
  }

  searchSites(url): Observable<any> {
    return this.http.get(url);
  }

  saveUser(url, params): Observable<any> {
    return this.http.post(url, params);
  }

  editUser(url, params): Observable<any> {
    return this.http.put(url, params);
  }

  deleteUser(url, params): Observable<any> {
    return this.http.delete(url, params);
  }
  restrictUser(url): Observable<any> {
    return this.http.get(url);
  }
  restrictUser1(url1): Observable<any> {
    return this.http.get(url1);
  }
  saveRestrictUser(url, params): Observable<any> {
    return this.http.post(url, params);
  }

  getNewsletterData(potentialId: string, fromDate: string, toDate: string): Observable<any> {
    const url = `${environment.workSpaceAPI}/newsletter/data?potentialId=${potentialId}&fromDate=${fromDate}&toDate=${toDate}`;
    return this.http.get<any>(url);
  }

  private get cfCheckerBaseUrl(): string {
    return environment.cfCheckerAPI;
  }

  getRtspCheck(potentialId: string): Observable<any> {
    const url = `${this.cfCheckerBaseUrl}/rtsp-check`;
    return this.http.post<any>(url, { potential_id: potentialId });
  }

  getPacketLossCheck(potentialId: string): Observable<any> {
    const url = `${this.cfCheckerBaseUrl}/packet-loss-check`;
    return this.http.post<any>(url, { potential_id: potentialId });
  }

  getDockerServiceCheck(potentialId: string): Observable<any> {
    const url = `${this.cfCheckerBaseUrl}/docker-service-check`;
    return this.http.post<any>(url, { potential_id: potentialId });
  }

  getSystemResourceCheck(potentialId: string): Observable<any> {
    const url = `${this.cfCheckerBaseUrl}/system-resource-check`;
    return this.http.post<any>(url, { potential_id: potentialId });
  }
}